import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  DoughnutController,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  LogarithmicScale,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js';
/**
 * Date adapter for `TimeScale`. Chart.js ships the scale but no date library:
 * without an adapter a time axis registers cleanly and then throws
 * "This method is not implemented" the first time it renders. dayjs is already
 * a dependency and is already configured in `@/lib/dayjs-init`, so this adapter
 * adds no new runtime.
 */
import 'chartjs-adapter-dayjs-4';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { configMarkerPlugin } from './chart-config-marker-plugin';
import { renderAfterFirstUpdatePlugin } from './chart-render-after-first-update';

/**
 * Register all Chart.js components needed for charts.
 *
 * This module is imported for its side effects exactly once per runtime, from
 * `src/next-app/client/runtime-providers.tsx`, `src/providers/app/app-provider.tsx`
 * and `.storybook/preview.tsx`. `ChartContainer` also imports it so a chart
 * rendered outside those providers still gets a populated registry.
 *
 * ---
 *
 * **Elements are not controllers, and a chart needs both.** `BarElement` is the
 * rectangle Chart.js draws; `BarController` is the dataset controller that
 * decides there should be rectangles at all and drives `type: 'bar'`. They are
 * separate registrations in Chart.js v4, and registering only the element
 * leaves `type: 'bar'` throwing `"bar" is not a registered controller.` the
 * first time it renders. Same split for `LineElement` / `LineController`.
 *
 * **Why that shipped to production undetected.** `react-chartjs-2` exports
 * typed components (`<Bar>`, `<Line>`) that register their own controller as an
 * import side effect, and a generic `<Chart type="bar">` that does not. The
 * product's stacked breakdown uses the generic `<Chart>`
 * (`profile-status-breakdown-chart.tsx`); the Storybook story that reviewed it
 * imports the typed `<Bar>`. So the story silently registered `BarController`
 * for itself and rendered a registry the product never had — every design round
 * passed on a chart the application could not draw. A Storybook render is not
 * evidence that a chart type is registered.
 *
 * Register the controllers the app actually renders, not all of them:
 * `chart-init.test.ts` derives the rendered set from source and fails if a
 * chart file names a type with no controller here, which is what makes this
 * list safe to keep short. That scan reads the forms this codebase uses —
 * `type:` in a config or dataset object, `type=` on a generic `<Chart>`, and
 * typed `react-chartjs-2` imports. A type reached some other way (a variable,
 * a spread config, an imperative `chart.config.type`) is invisible to it, so
 * extend the scan alongside any such callsite rather than assuming it is
 * covered.
 */
ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  TimeScale,
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  /*
   * Inert today: `Filler` only does work for a dataset that sets `fill`, and
   * nothing here does — the dominance reference is an unfilled dashed line and
   * Chart.js v4 defaults line datasets to `fill: false`. Kept rather than
   * removed because it is a plugin with no controller to pair with, so it
   * cannot cause the failure this file exists to prevent.
   */
  Filler,
  /*
   * The doughnut pair, added for the dashboard's Tier A distribution widgets
   * (W2, W4). Both, and for the reason the paragraph above spells out:
   * `DoughnutController` drives `type: 'doughnut'` and `ArcElement` is the
   * shape it draws, and Chart.js resolves the element through
   * `registry.getElement('arc')` at first render and throws when it is absent.
   *
   * An earlier revision of this file removed `ArcElement` precisely because no
   * arc controller was registered, so it read as though something drew arcs.
   * That reasoning was right then and is why both land together now.
   *
   * Registering the controller HERE rather than relying on react-chartjs-2's
   * typed `<Doughnut>` to self-register is the whole lesson of the paragraph
   * above: a typed import registers a controller for whichever bundle imported
   * it, so a Storybook story renders a registry the product does not have. It
   * is also what `chart-init.test.ts` asserts — its source scan finds typed
   * react-chartjs-2 imports and requires a controller here for each.
   */
  ArcElement,
  DoughnutController,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  /*
   * Draws nothing: it cancels a render Chart.js issues before the chart's
   * first `update()`, which is the draw that reaches `ChartDataLabels`'
   * `afterDatasetsDraw` with nothing laid out yet (ENG-3184). See the module
   * for why `resizeDelay` is what exposes it.
   *
   * Its position after `ChartDataLabels` is incidental. Chart.js runs every
   * plugin's `afterUpdate` in one synchronous notification pass before it can
   * render or draw, while a cancelled `beforeRender` returns out of `render()`
   * at any registration position. The reverse-order behavior is covered in
   * `chart-render-after-first-update.test.ts`.
   */
  renderAfterFirstUpdatePlugin,
  /*
   * ENG-3109's configuration-change marker overlay. Registered globally, like
   * every plugin here, and inert on every chart that does not put a `points`
   * array into its own `plugins.minervaConfigMarkers` options.
   */
  configMarkerPlugin
);

/**
 * `chartjs-plugin-datalabels` draws on every dataset of every chart as soon as
 * it is registered, and registration is global. Turning it off by default
 * keeps it opt-in for the D67 spread treatment on unstacked point charts.
 * Stacked charts use their chart-local measured leader-label plugin instead,
 * so a reference or value label never enters datalabels' in-plot layout.
 */
ChartJS.defaults.set('plugins.datalabels', { display: false });
